import { MemoizedSelector, Selector } from '@ngrx/store';

import { getMetaAdapter, getMetaSelectors } from '../meta-adapter';
import { createSimpleSelector, namedProjector } from '../selector-utils';
import { Reference } from '../types';
import { createSelectorWithReferences } from '../utils';
import { getSingleEntityActions } from './actions';
import { getSingleEntityDataAdapter } from './data';
import { NotNull, SingleEntityAdapter, SingleEntityState } from './types';

const singleEntityInitialState: SingleEntityState<any> = Object.freeze({
  data: null,
  loaded: false,
  pending: false,
  failed: false,
  error: null,
} as const);

export const createSingleEntityAdapter = <T extends object | null>(
  baseSelector: Selector<any, SingleEntityState<T>>,
  namespace: string,
  references: Reference<NotNull<T>>[] = [],
) => {
  const adapter: SingleEntityAdapter<T> = {
    ...getMetaAdapter<SingleEntityState<T>>(),
    ...getSingleEntityDataAdapter(singleEntityInitialState),
  };

  const defaultActions = getSingleEntityActions<T>(adapter);
  const dataSelector = createSimpleSelector(
    baseSelector,
    namedProjector(`${namespace}Data`, state => state.data),
  );
  const dataWithRefsSelector = <MemoizedSelector<any, T>>createSelectorWithReferences(
    dataSelector,
    references,
    namedProjector(`${namespace}Data`, data => data),
  );

  const selectors = {
    ...getMetaSelectors(baseSelector, namespace),
    data: references.length ? dataWithRefsSelector : dataSelector,
    state: createSimpleSelector(
      baseSelector,
      namedProjector(`${namespace}State`, (state: SingleEntityState<T>) => state),
    ),
  };

  return {
    adapter,
    selectors,
    defaultActions,
    initialState: <SingleEntityState<T>>singleEntityInitialState,
  };
};
